import {
  ETextStyleVariant,
  ETooltipVariant,
  GeneralAlertCircleIcon,
  GeneralCurrencyMoneyIcon,
  Text,
  Tooltip,
} from '@outdoorsyco/bonfire';
import { clsx } from 'clsx';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { useBillSelectionStep } from '@/components/route/listing/BillSectionSelection/BillSelectionStep/useBillSelectionStep';
import { TRootState } from '@/redux/rootReducer';
import { getBookingOrQuoteTotal } from '@/redux/selectors/bill/total';
import { getQuoteCurrency, getQuoteIsLoading } from '@/redux/selectors/quote';
import { formatCurrency } from '@/utility/currency';

interface IProps {
  lastStep?: boolean;
}

export const BillRewardsBanner: React.FC<IProps> = ({ lastStep = false }) => {
  const intl = useIntl();
  const { datesSelection } = useBillSelectionStep();
  const total = useSelector((state: TRootState) => getBookingOrQuoteTotal(state));
  const currency = useSelector(getQuoteCurrency);
  const getLoyaltyRewardsText = useCallback(
    (totalAmount: string) => {
      // Remove any currency symbols and convert to number
      const numericAmount = parseFloat(totalAmount.replace(/[^0-9.-]+/g, ''));
      const actualAmmount = Math.round(numericAmount * 100 * 0.1);
      // Calculate 10% and format as currency
      const maxAmount = formatCurrency({
        priceInCents: actualAmmount > 10000 ? 10000 : actualAmmount, // Convert to cents and calculate 10%
        currency: currency,
        digits: 2,
      });

      return intl.formatMessage(
        {
          id: '0sBat/',
          defaultMessage: 'Earn <ammount>{max}</ammount> for use on your next trip',
          description: 'checkout - loyalty - cta',
        },
        {
          max: maxAmount,
          ammount: (label: React.ReactNode) => (
            <Text variant={ETextStyleVariant.SmallBold} className="inline" component="span">
              {label}
            </Text>
          ),
        },
      );
    },
    [intl, currency],
  );
  const quoteIsLoading = useSelector(getQuoteIsLoading);
  const hasDatesSelected = Boolean(datesSelection?.from && datesSelection?.to);
  const shouldShowTotal = hasDatesSelected && !quoteIsLoading;
  const [loyaltyRewardsText, setLoyaltyRewardsText] = useState(
    shouldShowTotal && total
      ? getLoyaltyRewardsText(total)
      : intl.formatMessage(
          {
            id: 'N+3QH5',
            defaultMessage: 'Earn up to <ammount>{max}</ammount> off of your next trip',
            description: 'checkout - loyalty - cta',
          },
          {
            max: '$100',
            ammount: (label: React.ReactNode) => (
              <Text variant={ETextStyleVariant.SmallBold} className="inline" component="span">
                {label}
              </Text>
            ),
          },
        ),
  );

  useEffect(() => {
    if (shouldShowTotal && total) {
      setLoyaltyRewardsText(getLoyaltyRewardsText(total));
    }
  }, [shouldShowTotal, total, quoteIsLoading, getLoyaltyRewardsText]);

  return (
    <div
      className={clsx('items-center p-4 flex gap-3 bg-grey-100 rounded-2xl', {
        'my-2': !lastStep && !shouldShowTotal,
        'my-4': lastStep || shouldShowTotal,
      })}>
      <div>
        <GeneralCurrencyMoneyIcon fontSize={16} />
      </div>
      <div className="flex-1">
        <Text className="flex-1 text-start" variant={ETextStyleVariant.SmallBold}>
          <FormattedMessage defaultMessage="Book now to earn rewards" id="z7iC0W" />
        </Text>
        <Text className="flex-1 text-start" variant={ETextStyleVariant.SmallRegular}>
          {loyaltyRewardsText}
        </Text>
      </div>
      <div className="flex justify-end">
        <Tooltip
          content={intl.formatMessage({
            defaultMessage:
              'By booking today, you will earn 10% of the value of this booking (up to $100) off your next booking! Reward will be granted when your vacation starts, and be valid for 12 months from the date it is granted.',
            id: 'eovJgY',
          })}
          placement="top-end"
          variant={ETooltipVariant.Default}
          arrow={false}>
          <GeneralAlertCircleIcon fontSize={16} />
        </Tooltip>
      </div>
    </div>
  );
};
